/**
 * Accept an organization invitation.
 *
 * This screen exists in the auth portal rather than in a module because it is
 * the only place both sign-in paths work: a password account and a Google
 * account are both established here, and only here can an invitation take
 * precedence over the domain and generic-provider rules that would otherwise
 * decide where a new user belongs (PRD §7.4).
 *
 * Four states, driven by what the visitor already has:
 *
 *  - no session          → show what the invitation is for, offer sign-up/Google
 *  - session, matching   → accept, then hand off to the module
 *  - session, mismatched → say so plainly and offer to switch account
 *  - bad or expired link → one message, identical for every cause
 *
 * The invited address is deliberately not editable. Acceptance is refused
 * server-side unless the signed-in address matches, so an editable field would
 * only invite a confusing failure two steps later.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { OrganizationSummary } from '@mindpeer/auth-shared';
import { AuthLayout } from '@/components/auth-layout';
import {
  DarkAlert,
  DarkButton,
  DarkDivider,
  GoogleMark,
  Spinner,
} from '@/components/ui';
import { useAuth } from '@/hooks/use-auth';
import { ApiError, api } from '@/lib/api';
import { googleErrorFrom, startGoogleSignIn } from '@/lib/google-sign-in';

interface InvitationDetails {
  email: string;
  roles: string[];
  expiresAt: string;
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
  organization: OrganizationSummary;
  continueUrl: string | null;
}

/** "admin" and "owner" both mean "can run this organization" to a reader. */
function describeRoles(roles: string[]): string {
  if (roles.includes('owner') || roles.includes('admin')) return 'as an administrator';
  if (roles.includes('viewer')) return 'with view-only access';
  return 'as a member';
}

export function AcceptInvitationPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { user, loading: authLoading, refresh } = useAuth();

  const [details, setDetails] = useState<InvitationDetails | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(true);

  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [accepted, setAccepted] = useState<{
    organization: OrganizationSummary;
    continueUrl: string | null;
  } | null>(null);

  // Set by the server when it sends the browser back here after a Google
  // sign-in that did not work out; this page is re-mounted by that redirect.
  const googleError = googleErrorFrom(params.toString());
  const [googleLoading, setGoogleLoading] = useState(false);

  /* ---------------------------------------------------------------- *
   * Load what the invitation is for
   * ---------------------------------------------------------------- */

  useEffect(() => {
    if (!token) {
      setLinkError('This invitation link is incomplete. Ask your administrator to send a new one.');
      setLoadingDetails(false);
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const result = await api.invitation(token);
        if (cancelled) return;
        setDetails(result);
        // Signing in with Google consumes a pending invitation during login,
        // so arriving back here with a spent token is the success path, not a
        // failure. Skip straight to the confirmation.
        if (result.status === 'accepted') {
          setAccepted({ organization: result.organization, continueUrl: result.continueUrl });
        }
      } catch (error) {
        if (cancelled) return;
        // Expired, revoked, already accepted and never-existed all land here
        // with the same message — the server does not distinguish them either.
        setLinkError(
          error instanceof ApiError && error.status === 404
            ? 'This invitation is no longer valid. It may have expired or already been used. Ask your administrator to send a new one.'
            : 'We could not check this invitation just now. Please try again in a moment.',
        );
      } finally {
        if (!cancelled) setLoadingDetails(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  /* ---------------------------------------------------------------- *
   * Accept, once there is a session on the invited address
   * ---------------------------------------------------------------- */

  const matches =
    !!user &&
    !!details &&
    details.status === 'pending' &&
    user.email.toLowerCase() === details.email.toLowerCase();

  const accept = useCallback(async () => {
    setAccepting(true);
    setAcceptError(null);
    try {
      const result = await api.acceptInvitation(token);
      // The accepted organization becomes the session's active one, so the
      // cached user is now stale in a way the next screen would notice.
      await refresh();
      setAccepted(result);
    } catch (error) {
      setAcceptError(
        error instanceof ApiError
          ? error.message
          : 'We could not accept this invitation just now. Please try again.',
      );
    } finally {
      setAccepting(false);
    }
  }, [token, refresh]);

  // Someone arriving already signed in on the right address has nothing to
  // decide, so acceptance happens without making them press a second button.
  useEffect(() => {
    if (matches && !accepted && !accepting && !acceptError) void accept();
  }, [matches, accepted, accepting, acceptError, accept]);

  const signInWithGoogle = () => {
    setGoogleLoading(true);
    startGoogleSignIn({
      // Come back to this same invitation. The token is what ties the invitee
      // to their own tenant — without it they would be resolved by email
      // domain instead, which for a Gmail address means the Global
      // organization. The server keeps this in its signed state cookie, so it
      // travels to Google and back without ever being in a URL Google sees.
      next: `/accept-invitation?token=${encodeURIComponent(token)}`,
      email: details?.email ?? null,
    });
  };

  /* ---------------------------------------------------------------- *
   * Render
   * ---------------------------------------------------------------- */

  if (loadingDetails || authLoading) {
    return (
      <AuthLayout title="Checking your invitation">
        <div className="flex items-center gap-3 text-sm text-slate-400" role="status">
          <Spinner className="size-5 text-brand-600" />
          One moment…
        </div>
      </AuthLayout>
    );
  }

  if (linkError || !details) {
    return (
      <AuthLayout
        title="This invitation cannot be used"
        description="Invitations are single-use and expire, so this link may simply be out of date."
        footer={
          <Link to="/sign-in" className="font-medium text-brand-400 hover:text-brand-300">
            Go to sign in
          </Link>
        }
      >
        <DarkAlert tone="error">{linkError}</DarkAlert>
      </AuthLayout>
    );
  }

  const orgName = details.organization.name;

  if (accepted) {
    return (
      <AuthLayout
        title={`You have joined ${accepted.organization.name}`}
        description="Your account is ready."
        footer={
          <Link to="/account" className="font-medium text-brand-400 hover:text-brand-300">
            View your account
          </Link>
        }
      >
        <DarkAlert tone="success">
          You are now a member of <strong>{accepted.organization.name}</strong>.
        </DarkAlert>
        {accepted.continueUrl ? (
          <DarkButton onClick={() => window.location.assign(accepted.continueUrl!)}>
            Continue to MindPeer
          </DarkButton>
        ) : (
          /**
           * No entitled application. Saying "contact your administrator" is
           * more honest than a button that would dead-end at an access denial.
           */
          <DarkAlert tone="info">
            Your organization does not have access to a MindPeer module yet. Your administrator can
            enable one.
          </DarkAlert>
        )}
      </AuthLayout>
    );
  }

  // Signed in, but as somebody else.
  if (user && !matches) {
    return (
      <AuthLayout
        title="This invitation is for a different account"
        description={`It was sent to ${details.email}, but you are signed in as ${user.email}.`}
        footer={
          <Link to="/sign-out" className="font-medium text-brand-400 hover:text-brand-300">
            Sign out and use the invited address
          </Link>
        }
      >
        <DarkAlert tone="warning">
          Sign out and sign back in as <strong>{details.email}</strong> to join {orgName}.
        </DarkAlert>
      </AuthLayout>
    );
  }

  if (matches) {
    return (
      <AuthLayout title={`Joining ${orgName}`} description="One moment.">
        {acceptError ? (
          <>
            <DarkAlert tone="error">{acceptError}</DarkAlert>
            <DarkButton onClick={() => void accept()} disabled={accepting}>
              Try again
            </DarkButton>
          </>
        ) : (
          <div className="flex items-center gap-3 text-sm text-slate-400" role="status">
            <Spinner className="size-5 text-brand-600" />
            Adding you to {orgName}…
          </div>
        )}
      </AuthLayout>
    );
  }

  // No session: this is the common case — an invitee with no account yet.
  return (
    <AuthLayout
      title={`Join ${orgName} on MindPeer`}
      description={
        <>
          You have been invited {describeRoles(details.roles)} as <strong>{details.email}</strong>.
        </>
      }
      footer={
        <>
          Already have a MindPeer account?{' '}
          <Link
            to={`/sign-in?email=${encodeURIComponent(details.email)}&next=${encodeURIComponent(
              `/accept-invitation?token=${token}`,
            )}`}
            className="font-medium text-brand-400 hover:text-brand-300"
          >
            Sign in
          </Link>
        </>
      }
    >
      {googleError && <DarkAlert tone="error">{googleError}</DarkAlert>}

      <DarkAlert tone="info">
        Use <strong>{details.email}</strong> — the invitation is tied to that address.
      </DarkAlert>

      <Link
        to={`/sign-up?invitation=${encodeURIComponent(token)}&email=${encodeURIComponent(
          details.email,
        )}`}
        className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-linear-to-r from-brand-600 to-brand-500 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-brand-900/40 transition-[filter] hover:brightness-110"
      >
        Create an account
      </Link>

      <DarkDivider label="or" />

      <DarkButton loading={googleLoading} onClick={signInWithGoogle}>
        <GoogleMark />
        Continue with Google
      </DarkButton>
    </AuthLayout>
  );
}
